<template>
    <div class="page-container" :class="theme">
        <canvas class="particles"></canvas>

        <button class="theme-toggle" @click="toggleTheme" :aria-label="theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'">
            <svg v-if="theme === 'dark'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
        </button>

        <button class="back-button" @click="$router.push('/')">← Back</button>
        <div class="progress-bar" :style="{ width: scrollProgress + '%' }"></div>

        <div class="main-container">
            <header class="section-header">
                <h1 class="section-title">Experience</h1>
                <div class="section-divider"></div>
            </header>

            <div class="timeline">
                <div class="internship-card"
                     v-for="(internship, index) in internships"
                     :key="index"
                     :style="{ animationDelay: index * 0.12 + 's' }"
                     :class="{ active: activeIndex === index }">

                    <div class="card-header" @click="toggleInternship(index)">
                        <img v-if="internship.logo" :src="internship.logo" :alt="internship.company" class="company-logo">
                        <div class="card-meta">
                            <span class="company-name">{{ internship.company }}</span>
                            <span class="position-title">{{ internship.position }}</span>
                        </div>
                        <div class="card-right">
                            <span class="date-badge">{{ internship.period }}</span>
                            <svg class="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <polyline points="6 9 12 15 18 9"/>
                            </svg>
                        </div>
                    </div>

                    <transition name="slide-fade" @enter="startTransition" @leave="endTransition">
                        <div v-show="activeIndex === index"
                             class="dropdown-content"
                             :class="{ visible: activeIndex === index }"
                             @click.stop>
                            <ul class="description-list">
                                <li v-for="(point, pointIndex) in internship.description"
                                    :key="pointIndex"
                                    class="description-item">
                                    {{ point }}
                                </li>
                            </ul>
                            <div class="tech-stack" v-if="internship.technologies && internship.technologies.length">
                                <div class="tech-tags">
                                    <span v-for="(tech, techIndex) in internship.technologies"
                                          :key="techIndex"
                                          class="tech-tag">{{ tech }}</span>
                                </div>
                            </div>
                            <div v-if="internship.projectLink" class="project-link">
                                <a :href="internship.projectLink" target="_blank" rel="noopener noreferrer" class="link-button">
                                    View Project ↗
                                </a>
                            </div>
                            <div class="gallery" v-if="internship.images && internship.images.length">
                                <img v-for="(image, imgIndex) in internship.images"
                                     :key="imgIndex"
                                     :src="image"
                                     class="gallery-image"
                                     alt="Work sample"
                                     loading="lazy">
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import UCLALogo from '@/assets/UCLA-HealthSystem-Logo-RGB.png';
import PIESLogo from '@/assets/pies.png';
import PIESLogo1 from '@/assets/pies1.png';
import UCLAHealth from '@/assets/UCLA_Health.png';
import UCLAHealth1 from '@/assets/UCLA_Health1.png';
import UCLAHealth2 from '@/assets/UCLA_Health2.png';
import AIA from '@/assets/aia-removebg-preview.png';
import aia_intro from '@/assets/aia_intro.png';
import aia_middle from '@/assets/aia_middle.png';
import qcsaLogo from '@/assets/QCSALOGO.png';
import fetch from '@/assets/fetch.png';
import original_protein from '@/assets/original_protein.png';
import preprocessing from '@/assets/preprocessing.png';
import CNN from '@/assets/CNN.png';
import quantumLayer from '@/assets/QuantumLayer.png';
import mainQCSA from '@/assets/mainQCSA.png';
import aboutQCSA from '@/assets/aboutQCSA.png';
import galleryQCSA from '@/assets/galleryQCSA.png';
import qmprot from '@/assets/qmprot.png';

export default {
    data() {
        return {
            theme: 'dark',
            internships: [
                {
                    company: "Freelance, Los Angeles",
                    position: "Student Researcher: Comparing VQEs with QMProt Dataset",
                    period: "May 2025 - June 2026",
                    logo: qmprot,
                    description: [
                        "Implemented Classically Boosted VQE (CB-VQE) using Hadamard test circuits to compute 2×2 projected Hamiltonian subspace, achieving consistent energy improvements (0.6–22 Ha) over standard VQE across 5 amino acid molecules through generalized eigenvalue post-processing",
                        "Implemented Hamiltonian Variational Ansatz (HVA) with greedy Pauli term partitioning into commuting groups, reducing variational parameters by 80% (6 vs 30) while maintaining 80% convergence rate through physics-informed time-evolution operators",
                        "Debugged critical Hamiltonian parsing pipeline that was dropping 99.9% of multi-qubit Pauli operators due to incorrect OpenFermion format handling, fixed active-space truncation wire remapping, and corrected HF reference energy calculations for truncated systems",
                        "Developed a comprehensive benchmarking framework to compare VQE, CB-VQE, and HVA performance on the QMProt dataset, analyzing convergence rates, energy accuracy, and computational resource requirements across 10+ molecular systems",
                        "Submitted an arXiv preprint on the findings, highlighting the advantages of CB-VQE and HVA for near-term quantum chemistry simulations and proposing future directions for hybrid quantum-classical algorithms",
                    ],
                    technologies: ["Python", "Pennylane", "Quantum Chemistry", "OpenFermion", "Git"],
                    projectLink: "https://github.com/sanskriti-ss/qmprot_gse",
                    images: [] // Add project images if available
                },
                  {
                    company: "Quantum Computing Student Association, UCLA",
                    position: "Tech Lead Full-Stack Developer",
                    period: "May 2025 - June 2026",
                    logo: qcsaLogo, 
                    description: [
                        "Collaborating with the QCSA Board and dev team on technical architecture and feature planning for organization's web platform using Next.js, TypeScript, Supabase, and Stripe integration",
                        "Contributing to full-stack development roadmap and UI/UX design decisions for student engagement platform launched Fall 2025: https://qcsa-ucla.org",
                        "Co-Lead to a Qiskit Fall Fest at UCLA and co-made its website through https://qiskit-fall-fest-ucla.vercel.app/. In this event, I've mainly handled most of the tech side + logistics during the event",
                        "Co-Organized to our recent event called QuARC (https://quarc.squarespace.com/). I've also helped its website, emailed speakers and participants regarding logistics and provided some participants fee waivers, and helped with tech maintenance during the event. Additionally, this amazing event had 150+ attendees with 12+ speakers from Google Quantum, IBM, Aqora, BlueQubit, Alice and Bob, and Quantum Elements!",
                        "Led development of a full-stack website for QDC, coordinating with maintainers, educators, and other industry leaders featuring GOOGLE and NVIDIA and deploying scalable features",
                    ],
                    technologies: ["Next.js", "TypeScript", "Supabase", "Stripe", "Vercel", "GitHub"],
                    projectLink: "https://qcsa-ucla.org",
                    images: [mainQCSA, aboutQCSA, galleryQCSA], 
                },
                {
                    company: "Fetch.ai",
                    position: "Student Researcher",
                    period: "May 2025 - June 2026",
                    logo: fetch, // Add Fetch.ai logo if available
                    description: [
                        "Preprocessed and validated 5200+ protein-ligand complexes from the 2021 PDBBind database, implementing comprehensive 3D voxelization pipeline to convert molecular structures into 48³ feature tensors with 19 biochemical properties per voxel for machine learning applications",
                        "Collaborated on fine-tuning classical 3D CNN architecture with 5 convolutional layers and residual connections to predict protein-ligand binding affinities, targeting adjusted R2 of 0.91+ for model evaluation metrics",
                        "Collaborated on a Fusion Model development, integrating 3D-CNN and SGCNN weight extraction with 2 quantum layers simulation using Pennylane framework to enhance binding energy prediction accuracy through quantum-classical hybrid approaches"
                    ],
                    technologies: ["Python", "OpenBabel", "Jupyter Notebooks", "RDKit", "3D-CNN", "Quantum Computing", "Chimera", "Pennylane"],
                    projectLink: "https://docs.google.com/presentation/d/1tRTiS26-27IkfnT4mmIx26rzXX2XfFug6AiHyBj-CK8/edit",
                    images: [original_protein, preprocessing, CNN, quantumLayer] // Add project images if available
                },
                {
                    company: "UCLA Health",
                    position: "Software Development Intern",
                    period: "Sept 2024 - May 2025",
                    logo: UCLALogo, 
                    description: [
                        "Developed AI application that translates unorganized blood pressure data into a cleaned and organized format",
                        "Utilized HandwrittenOCR tools, Next.js, and ChatGPT enterprise models",
                        "Focused on improving data processing efficiency for healthcare providers",
                        "Collaborated with a team of 4 developers and 2 doctors",
                        "Presented the application to healthcare executives in May 2025, received positive feedback",
                    ],
                    technologies: ["Next.js", "OCR", "Typescript", "Python"],
                    images: [UCLAHealth, UCLAHealth1, UCLAHealth2],
                    projectLink: "https://blood-pressure-ocr.vercel.app"
                },
                {
                    company: "Pilipinos In Engineering and Sciences",
                    position: "Projects Co-Developer",
                    period: "Jan 2025 - May 2025",
                    logo: PIESLogo,
                    description: [
                        "Co-developed 'Liwanag', an interactive Tagalog learning app using character-driven storytelling",
                        "Built the application with React Native and TypeScript",
                        "Revamped the PIES website with improved UI/UX design in TypeScript and Next.js",
                        "Collaborated with a team of 3 frontend developers, 4 backend developers, and other interns",
                        "The Project is currently in the development phase and will be continued in July 2025"
                    ],
                    technologies: ["React Native", "TypeScript", "Next.js"],
                    images: [PIESLogo1]
                },
                {
                    company: "The American Institute of Architects (AIA)",
                    position: "Project Lead Software Development",
                    period: "Jun 2025 - August 2025",
                    logo: AIA, 
                    description: [
                        "Led as project lead for a 3+ person team, driving development of a Next.js-based architect discovery platform (Palisades Architects Connection)",
                        "Implemented CI/CD workflows on Vercel and git, collaborating directly with AIA developers and designers to align feature requirements and target a summer 2025 deployment",
                        "Facilitated cross-functional communication—setting milestones, coordinating frontend/backend efforts, and ensuring the site is on track for deployment"
                    ],
                    technologies: ["Next.js", "Vercel", "Git", "CI/CD"],
                    images: [aia_intro, aia_middle],
                    projectLink: "https://architectsconnect.vercel.app"
                },
              
              
            ],
            activeIndex: null,
            scrollProgress: 0
        }
    },
    mounted() {
        this.initParticles();
        window.addEventListener('scroll', this.updateScrollProgress);
        this.theme = localStorage.getItem('theme') || 'dark';
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.updateScrollProgress);
    },
    methods: {
        toggleInternship(index) {
            this.activeIndex = this.activeIndex === index ? null : index;
        },
        startTransition(el) {
            const height = el.scrollHeight;
            el.style.height = '0';
            el.offsetHeight; // force reflow
            el.style.height = height + 'px';
        },
        endTransition(el) {
            el.style.height = el.scrollHeight + 'px';
            el.offsetHeight; // force reflow
            el.style.height = '0';
        },
        initParticles() {
            const canvas = document.querySelector('.particles');
            if (!canvas) return;
            
            const ctx = canvas.getContext('2d');
            const particles = [];
            
            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            
            window.addEventListener('resize', resizeCanvas);
            resizeCanvas();

            class Particle {
                constructor() {
                    this.x = Math.random() * canvas.width;
                    this.y = Math.random() * canvas.height;
                    this.size = Math.random() * 3 + 1;
                    this.speedX = Math.random() * 3 - 1.5;
                    this.speedY = Math.random() * 3 - 1.5;
                    this.opacity = Math.random() * 0.5 + 0.3;
                }

                update() {
                    this.x += this.speedX;
                    this.y += this.speedY;

                    if (this.x > canvas.width) this.x = 0;
                    if (this.x < 0) this.x = canvas.width;
                    if (this.y > canvas.height) this.y = 0;
                    if (this.y < 0) this.y = canvas.height;
                }

                draw() {
                    ctx.fillStyle = `rgba(52, 152, 219, ${this.opacity})`;
                    ctx.beginPath();
                    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            const createParticles = () => {
                for (let i = 0; i < 50; i++) {
                    particles.push(new Particle());
                }
            };

            const animate = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                particles.forEach(particle => {
                    particle.update();
                    particle.draw();
                });
                requestAnimationFrame(animate);
            };

            createParticles();
            animate();
        },
        updateScrollProgress() {
            const scrollTop = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            this.scrollProgress = (scrollTop / docHeight) * 100;
        },
        toggleTheme() {
            this.theme = this.theme === 'dark' ? 'light' : 'dark';
            localStorage.setItem('theme', this.theme);
        }
    }
}
</script>

<style scoped>
/* =====================
   CSS Variables / Theming
   ===================== */
.page-container {
    min-height: 100vh;
    position: relative;
    transition: background-color 0.3s ease, color 0.3s ease;
}

.page-container.dark {
    --bg: #0d2535;
    --text-primary: #cce0ec;
    --text-secondary: #7aaabf;
    --card-bg: rgba(255, 255, 255, 0.03);
    --card-border: rgba(255, 255, 255, 0.07);
    --card-hover-border: rgba(74, 159, 200, 0.35);
    --accent: #5eb8de;
    --accent-subtle: rgba(74, 159, 200, 0.1);
    --accent-border: rgba(74, 159, 200, 0.28);
    --tag-text: #7dd3f0;
    --ui-border: rgba(255, 255, 255, 0.1);
    --ui-bg: rgba(255, 255, 255, 0.06);
    --ui-bg-hover: rgba(255, 255, 255, 0.11);
    background-color: var(--bg);
    color: var(--text-primary);
}

.page-container.light {
    --bg: #f0f5f9;
    --text-primary: #182635;
    --text-secondary: #4a6678;
    --card-bg: rgba(255, 255, 255, 0.9);
    --card-border: rgba(0, 0, 0, 0.08);
    --card-hover-border: rgba(26, 111, 160, 0.3);
    --accent: #1a6fa0;
    --accent-subtle: rgba(26, 111, 160, 0.08);
    --accent-border: rgba(26, 111, 160, 0.25);
    --tag-text: #1a6fa0;
    --ui-border: rgba(0, 0, 0, 0.1);
    --ui-bg: rgba(0, 0, 0, 0.04);
    --ui-bg-hover: rgba(0, 0, 0, 0.08);
    background-color: var(--bg);
    color: var(--text-primary);
}

/* =====================
   Fixed UI Elements
   ===================== */
.particles {
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    pointer-events: none;
    z-index: 0;
}

.progress-bar {
    position: fixed;
    top: 0; left: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent), transparent);
    z-index: 101;
    transition: width 0.1s linear;
}

.back-button,
.theme-toggle {
    position: fixed;
    top: 20px;
    background: var(--ui-bg);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid var(--ui-border);
    border-radius: 7px;
    color: var(--text-primary);
    cursor: pointer;
    z-index: 100;
    transition: background 0.2s ease, border-color 0.2s ease;
    font-family: inherit;
}

.back-button {
    left: 20px;
    padding: 0.45rem 1rem;
    font-size: 0.875rem;
}

.theme-toggle {
    right: 20px;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.back-button:hover,
.theme-toggle:hover {
    background: var(--ui-bg-hover);
    border-color: var(--accent-border);
}

.theme-toggle svg {
    width: 17px;
    height: 17px;
    stroke: var(--text-primary);
}

/* =====================
   Layout
   ===================== */
.main-container {
    max-width: 860px;
    margin: 0 auto;
    padding: 5.5rem 2rem 4rem;
    position: relative;
    z-index: 1;
}

/* =====================
   Section Header
   ===================== */
.section-header {
    margin-bottom: 2.5rem;
}

.section-title {
    font-size: 1.9rem;
    font-weight: 700;
    letter-spacing: -0.025em;
    margin: 0 0 0.6rem;
    color: var(--text-primary);
}

.section-divider {
    height: 2px;
    width: 48px;
    background: linear-gradient(90deg, var(--accent), transparent);
    border-radius: 2px;
}

/* =====================
   Timeline / Cards
   ===================== */
.timeline {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.internship-card {
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-left: 3px solid transparent;
    border-radius: 10px;
    overflow: hidden;
    opacity: 0;
    transform: translateY(14px);
    animation: fadeInUp 0.45s ease forwards;
    transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.internship-card:hover,
.internship-card.active {
    border-left-color: var(--accent);
    border-color: var(--card-hover-border);
    box-shadow: 0 4px 24px rgba(74, 159, 200, 0.08);
}

/* =====================
   Card Header
   ===================== */
.card-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.1rem 1.4rem;
    cursor: pointer;
    user-select: none;
}

.company-logo {
    width: 34px;
    height: 34px;
    object-fit: contain;
    border-radius: 6px;
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.07);
    padding: 3px;
}

.card-meta {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.18rem;
}

.company-name {
    font-size: 0.975rem;
    font-weight: 600;
    color: var(--accent);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.position-title {
    font-size: 0.85rem;
    color: var(--text-primary);
    opacity: 0.72;
}

.card-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.35rem;
    flex-shrink: 0;
}

.date-badge {
    font-size: 0.72rem;
    color: var(--text-secondary);
    white-space: nowrap;
    letter-spacing: 0.01em;
    font-weight: 500;
}

.chevron {
    width: 15px;
    height: 15px;
    color: var(--text-secondary);
    transition: transform 0.25s ease;
}

.internship-card.active .chevron {
    transform: rotate(180deg);
}

/* =====================
   Dropdown Content
   ===================== */
.dropdown-content {
    overflow: hidden;
    height: 0;
    transition: height 0.3s ease-out;
}

.dropdown-content.visible {
    height: auto;
    padding: 0 1.4rem 1.4rem;
}

/* =====================
   Description List
   ===================== */
.description-list {
    list-style: none;
    padding: 0;
    margin: 0 0 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
    border-top: 1px solid var(--card-border);
    padding-top: 1rem;
}

.description-item {
    font-size: 0.875rem;
    line-height: 1.75;
    color: var(--text-secondary);
    padding-left: 1.1rem;
    position: relative;
}

.description-item::before {
    content: '▸';
    position: absolute;
    left: 0;
    top: 0.1em;
    color: var(--accent);
    font-size: 0.7rem;
}

/* =====================
   Tech Tags
   ===================== */
.tech-stack {
    margin-bottom: 1rem;
}

.tech-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
}

.tech-tag {
    font-size: 0.72rem;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-weight: 500;
    padding: 3px 9px;
    border-radius: 4px;
    background: var(--accent-subtle);
    border: 1px solid var(--accent-border);
    color: var(--tag-text);
    letter-spacing: 0.02em;
}

/* =====================
   Project Link
   ===================== */
.project-link {
    margin-bottom: 1rem;
}

.link-button {
    display: inline-block;
    font-size: 0.825rem;
    font-weight: 500;
    padding: 0.45rem 1.1rem;
    border-radius: 6px;
    text-decoration: none;
    background: var(--accent-subtle);
    border: 1px solid var(--accent-border);
    color: var(--tag-text);
    transition: background 0.2s ease, border-color 0.2s ease;
    letter-spacing: 0.01em;
}

.link-button:hover {
    background: rgba(74, 159, 200, 0.18);
    border-color: var(--accent);
}

/* =====================
   Gallery
   ===================== */
.gallery {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.65rem;
    margin-top: 0.75rem;
}

.gallery-image {
    width: 100%;
    max-height: 320px;
    border-radius: 7px;
    object-fit: contain;
    background: rgba(0, 0, 0, 0.06);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border: 1px solid var(--card-border);
}

.gallery-image:hover {
    transform: scale(1.03);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.gallery-image:only-child {
    grid-column: 1 / -1;
    max-height: 400px;
}

/* =====================
   Animations
   ===================== */
@keyframes fadeInUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: opacity 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    opacity: 0;
}

/* =====================
   Responsive
   ===================== */
@media (max-width: 640px) {
    .main-container {
        padding: 5rem 1rem 3rem;
    }

    .card-header {
        gap: 0.75rem;
        padding: 1rem;
    }

    .company-logo {
        width: 28px;
        height: 28px;
    }

    .card-right {
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
    }

    .gallery {
        grid-template-columns: 1fr;
    }

    .gallery-image {
        max-height: 260px;
    }
}
</style>
